import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import MainLayout from './MainLayout';
import TopNavBar from './TopNavBar';
import { useAuth } from '../context/AuthContext';
import { FiMapPin, FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiCrosshair } from 'react-icons/fi';
import {
    getOfficeLocations,
    createOfficeLocation,
    updateOfficeLocation,
    deleteOfficeLocation,
} from '../services/officeLocationService';
import { captureCurrentPosition } from '../utils/geolocation';
import '../styles/reportsettings.css';

const EMPTY_FORM = {
    name: '',
    address: '',
    latitude: '',
    longitude: '',
    radius_meters: 200,
    is_active: true,
};

export default function OfficeLocations({ isTab = false }) {
    const { user, token, logout } = useAuth();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);

    const fetchLocations = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            setLocations(await getOfficeLocations(token));
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'Failed to load office locations');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchLocations();
    }, [fetchLocations]);

    const resetForm = () => {
        setForm(EMPTY_FORM);
        setEditingId(null);
        setShowForm(false);
    };

    const startEdit = (location) => {
        setForm({
            name: location.name || '',
            address: location.address || '',
            latitude: location.latitude ?? '',
            longitude: location.longitude ?? '',
            radius_meters: location.radius_meters ?? 200,
            is_active: location.is_active !== false,
        });
        setEditingId(location.id);
        setShowForm(true);
    };

    /** Fill the coordinate fields from where the admin is standing right now. */
    const useMyLocation = async () => {
        const { coords, reason } = await captureCurrentPosition();
        if (!coords) return toast.error(reason || 'Could not read your location');
        setForm((prev) => ({
            ...prev,
            latitude: coords.latitude.toFixed(6),
            longitude: coords.longitude.toFixed(6),
        }));
        toast.success('Coordinates filled from your current position');
    };

    const handleSave = async () => {
        if (!form.name.trim()) return toast.error('Office name is required');
        if (form.latitude === '' || form.longitude === '') {
            return toast.error('Latitude and longitude are required');
        }

        const payload = {
            name: form.name.trim(),
            address: form.address?.trim() || null,
            latitude: Number(form.latitude),
            longitude: Number(form.longitude),
            radius_meters: Number(form.radius_meters),
            is_active: form.is_active,
        };

        setSaving(true);
        try {
            if (editingId) {
                await updateOfficeLocation(token, editingId, payload);
                toast.success('Office location updated');
            } else {
                await createOfficeLocation(token, payload);
                toast.success('Office location added');
            }
            resetForm();
            fetchLocations();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'Failed to save office location');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (location) => {
        if (!window.confirm(`Delete "${location.name}"? Check-ins will no longer match this office.`)) {
            return;
        }
        try {
            await deleteOfficeLocation(token, location.id);
            toast.success('Office location deleted');
            fetchLocations();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'Failed to delete office location');
        }
    };

    const content = (
        <>
        <div className="report-settings-page">
            {!isTab && (
                <div className="settings-header">
                    <div className="header-info">
                        <h1>Office Locations</h1>
                        <p>Check-ins within an office's radius are recorded as In Office, everything else as Out of Office</p>
                    </div>
                </div>
            )}

                <div className="settings-container">
                    <div className="settings-card">
                        <div className="card-header">
                            <FiMapPin className="card-icon" />
                            <span>Configured Offices</span>
                            {!showForm && (
                                <button className="ol-add-btn" onClick={() => { setForm(EMPTY_FORM); setShowForm(true); }}>
                                    <FiPlus /> Add Office
                                </button>
                            )}
                        </div>

                        <div className="card-body">
                            {showForm && (
                                <div className="ol-form">
                                    <div className="ol-form-grid">
                                        <label>
                                            <span>Office Name *</span>
                                            <input
                                                type="text"
                                                value={form.name}
                                                placeholder="e.g. Chennai HQ"
                                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                            />
                                        </label>
                                        <label>
                                            <span>Address</span>
                                            <input
                                                type="text"
                                                value={form.address}
                                                placeholder="Street, city"
                                                onChange={(e) => setForm({ ...form, address: e.target.value })}
                                            />
                                        </label>
                                        <label>
                                            <span>Latitude *</span>
                                            <input
                                                type="number"
                                                step="0.000001"
                                                value={form.latitude}
                                                placeholder="13.008000"
                                                onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                                            />
                                        </label>
                                        <label>
                                            <span>Longitude *</span>
                                            <input
                                                type="number"
                                                step="0.000001"
                                                value={form.longitude}
                                                placeholder="80.203500"
                                                onChange={(e) => setForm({ ...form, longitude: e.target.value })}
                                            />
                                        </label>
                                        <label>
                                            <span>Allowed Radius (metres) *</span>
                                            <input
                                                type="number"
                                                min="1"
                                                value={form.radius_meters}
                                                onChange={(e) => setForm({ ...form, radius_meters: e.target.value })}
                                            />
                                        </label>
                                        <label className="ol-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={form.is_active}
                                                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                                            />
                                            <span>Active (inactive offices are skipped when matching)</span>
                                        </label>
                                    </div>

                                    <div className="ol-form-actions">
                                        <button className="ol-secondary-btn" onClick={useMyLocation}>
                                            <FiCrosshair /> Use my current location
                                        </button>
                                        <div className="ol-form-actions-right">
                                            <button className="ol-secondary-btn" onClick={resetForm}>
                                                <FiX /> Cancel
                                            </button>
                                            <button className="btn-save" onClick={handleSave} disabled={saving}>
                                                {saving ? 'Saving...' : <><FiSave /> {editingId ? 'Update' : 'Add'} Office</>}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="ol-table-wrap">
                                <table className="ol-table">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Address</th>
                                            <th>Latitude</th>
                                            <th>Longitude</th>
                                            <th>Radius</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {locations.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="ol-empty">
                                                    {loading
                                                        ? 'Loading...'
                                                        : 'No offices configured yet. Until one is added, every located check-in is recorded as Out of Office.'}
                                                </td>
                                            </tr>
                                        ) : (
                                            locations.map((location) => (
                                                <tr key={location.id}>
                                                    <td>{location.name}</td>
                                                    <td>{location.address || '—'}</td>
                                                    <td className="ol-num">{Number(location.latitude).toFixed(6)}</td>
                                                    <td className="ol-num">{Number(location.longitude).toFixed(6)}</td>
                                                    <td className="ol-num">{location.radius_meters} m</td>
                                                    <td>
                                                        <span className={location.is_active !== false ? 'ol-pill ol-pill-active' : 'ol-pill ol-pill-inactive'}>
                                                            {location.is_active !== false ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <button className="ol-icon-btn" title="Edit" onClick={() => startEdit(location)}>
                                                            <FiEdit2 />
                                                        </button>
                                                        <button className="ol-icon-btn ol-icon-danger" title="Delete" onClick={() => handleDelete(location)}>
                                                            <FiTrash2 />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .ol-add-btn {
                    margin-left: auto;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 7px 14px;
                    border: none;
                    border-radius: 6px;
                    background: #0ea5e9;
                    color: #fff;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                }
                .ol-add-btn:hover { background: #0284c7; }

                .ol-form {
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 18px;
                    margin-bottom: 22px;
                    background: #f8fafc;
                }
                .ol-form-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
                    gap: 14px;
                }
                .ol-form-grid label {
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                    font-size: 13px;
                    color: #334155;
                    font-weight: 500;
                }
                .ol-form-grid input[type="text"],
                .ol-form-grid input[type="number"] {
                    padding: 8px 10px;
                    border: 1px solid #cbd5e1;
                    border-radius: 6px;
                    font-size: 14px;
                }
                .ol-form-grid input:focus {
                    outline: none;
                    border-color: #0ea5e9;
                }
                .ol-checkbox {
                    flex-direction: row !important;
                    align-items: center;
                    gap: 8px !important;
                }
                .ol-form-actions {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 10px;
                    margin-top: 18px;
                    flex-wrap: wrap;
                }
                .ol-form-actions-right { display: flex; gap: 10px; }
                .ol-secondary-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 9px 14px;
                    border: 1px solid #cbd5e1;
                    border-radius: 6px;
                    background: #fff;
                    color: #475569;
                    font-size: 13px;
                    font-weight: 500;
                    cursor: pointer;
                }
                .ol-secondary-btn:hover { background: #f1f5f9; }

                .ol-table-wrap { overflow-x: auto; }
                .ol-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 14px;
                    min-width: 760px;
                }
                .ol-table th {
                    text-align: left;
                    padding: 10px 12px;
                    font-size: 12px;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                    color: #64748b;
                    border-bottom: 1px solid #e2e8f0;
                }
                .ol-table td {
                    padding: 12px;
                    border-bottom: 1px solid #f1f5f9;
                    color: #1e293b;
                }
                .ol-num { font-variant-numeric: tabular-nums; }
                .ol-empty { text-align: center; color: #94a3b8; padding: 26px 12px; }

                .ol-pill {
                    display: inline-block;
                    padding: 3px 10px;
                    border-radius: 999px;
                    font-size: 12px;
                    font-weight: 600;
                }
                .ol-pill-active { background: #d1fae5; color: #047857; }
                .ol-pill-inactive { background: #e5e7eb; color: #6b7280; }

                .ol-icon-btn {
                    border: none;
                    background: transparent;
                    cursor: pointer;
                    padding: 6px;
                    color: #475569;
                    border-radius: 5px;
                }
                .ol-icon-btn:hover { background: #f1f5f9; color: #0ea5e9; }
                .ol-icon-danger:hover { color: #dc2626; }
            `}</style>
        </>
    );

    if (isTab) return content;

    return (
        <MainLayout sidebarOpen={sidebarOpen} toggleSidebar={() => setSidebarOpen(p => !p)}>
            {content}
        </MainLayout>
    );
}
